<template>
    <div>
        <!-- Credentials list -->
        <v-card elevation="3">
            <v-card-title class="text-start font-weight-bold d-flex align-center justify-space-between">
                <span>API Keys</span>
                <v-btn color="primary" size="small" prepend-icon="mdi-plus" @click="showCreate = true">
                    New API Key
                </v-btn>
            </v-card-title>
            <v-card-subtitle class="pb-2">
                Use API keys to give your own systems read-only access to fleet status. Keys are scoped to
                your company and cannot access any other data.
            </v-card-subtitle>

            <v-card-text class="pa-0">
                <v-progress-linear v-if="loading" indeterminate color="primary"/>

                <v-list v-if="credentials.length" lines="two" class="pa-0">
                    <v-list-item
                        v-for="cred in credentials"
                        :key="cred.id"
                        :subtitle="credSubtitle(cred)"
                    >
                        <template #prepend>
                            <v-icon icon="mdi-key-variant" color="primary" class="mr-2"/>
                        </template>
                        <template #title>
                            <span class="font-weight-medium">{{ cred.label }}</span>
                            <code class="text-caption text-medium-emphasis ml-2">{{ cred.token_prefix }}…</code>
                        </template>
                        <template #append>
                            <v-btn
                                variant="text"
                                color="error"
                                size="small"
                                icon="mdi-delete-outline"
                                :loading="revoking === cred.id"
                                @click="confirmRevoke(cred)"
                            />
                        </template>
                    </v-list-item>
                </v-list>

                <div v-else-if="!loading" class="pa-6 text-center text-medium-emphasis text-body-2">
                    No API keys yet. Create one to allow external systems to read your fleet status.
                </div>
            </v-card-text>
        </v-card>

        <!-- Usage reference -->
        <v-card elevation="2" class="mt-4 pa-4">
            <v-card-title class="text-body-2 font-weight-bold pb-2">API Reference</v-card-title>
            <div class="text-caption text-medium-emphasis mb-2">Authentication header:</div>
            <pre class="text-caption bg-grey-lighten-4 rounded pa-3 overflow-x-auto mb-3">Authorization: Bearer SGFLEET_&lt;your-key&gt;</pre>
            <div class="text-caption text-medium-emphasis mb-2">Available endpoints:</div>
            <pre class="text-caption bg-grey-lighten-4 rounded pa-3 overflow-x-auto mb-3">GET  {{ baseUrl }}/external/v1/assets              — list all assets with status
GET  {{ baseUrl }}/external/v1/assets/{id}          — single asset status
GET  {{ baseUrl }}/external/v1/fleet-counts         — moving / idle / stationary counts</pre>
            <div class="text-caption text-medium-emphasis mb-2">Example response (single asset):</div>
            <pre class="text-caption bg-grey-lighten-4 rounded pa-3 overflow-x-auto">{{ exampleResponse }}</pre>
            <div class="text-caption text-medium-emphasis mt-2">
                Rate limit: 120 requests/minute per key. Throttled responses return HTTP 429.
            </div>
        </v-card>

        <!-- Create dialog -->
        <v-dialog v-model="showCreate" max-width="480" persistent>
            <v-card>
                <v-card-title>New API Key</v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="newLabel"
                        label="Label"
                        placeholder="e.g. ERP Production, Dispatch System"
                        density="compact"
                        autofocus
                        :rules="[v => !!v || 'Label is required', v => v.length <= 100 || 'Max 100 characters']"
                        @keydown.enter="createCredential"
                    />
                    <div class="text-caption text-medium-emphasis">
                        The label helps you identify which system is using this key.
                    </div>
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-spacer/>
                    <v-btn variant="text" @click="cancelCreate">Cancel</v-btn>
                    <v-btn
                        color="primary"
                        :loading="creating"
                        :disabled="!newLabel.trim()"
                        @click="createCredential"
                    >
                        Create
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- One-time token reveal -->
        <v-dialog v-model="showToken" max-width="560" persistent>
            <v-card>
                <v-card-title class="d-flex align-center">
                    <v-icon icon="mdi-shield-key" color="primary" class="mr-2"/>
                    Save this key now
                </v-card-title>
                <v-card-text>
                    <v-alert type="warning" density="compact" class="mb-4" variant="tonal">
                        This token will <strong>not be shown again</strong>. Copy it before closing.
                    </v-alert>
                    <div class="text-caption text-medium-emphasis mb-1">API key for "{{ revealedLabel }}":</div>
                    <div class="d-flex align-center gap-2">
                        <code
                            class="flex-grow-1 text-caption bg-grey-lighten-4 rounded pa-3 d-block overflow-x-auto"
                            style="word-break: break-all"
                        >{{ revealedToken }}</code>
                        <v-btn
                            :icon="copied ? 'mdi-check' : 'mdi-content-copy'"
                            :color="copied ? 'success' : undefined"
                            variant="text"
                            size="small"
                            class="flex-shrink-0"
                            @click="copyToken"
                        />
                    </div>
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-spacer/>
                    <v-btn color="primary" @click="closeToken">
                        Done, I've copied it
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Revoke confirm dialog -->
        <v-dialog v-model="showRevokeConfirm" max-width="420">
            <v-card>
                <v-card-title>Revoke API Key?</v-card-title>
                <v-card-text>
                    <strong>{{ revokeTarget?.label }}</strong> will stop working immediately.
                    Any integration using it will receive 401 errors.
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-spacer/>
                    <v-btn variant="text" @click="showRevokeConfirm = false">Cancel</v-btn>
                    <v-btn color="error" :loading="revoking !== null" @click="doRevoke">Revoke</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup>
import {ref, onMounted} from 'vue';
import {send} from '@/composables/apiClient';
import {useSnackbarStore} from '@/stores/snackBarStore';

const snackbar = useSnackbarStore();

const credentials  = ref([]);
const loading      = ref(false);
const creating     = ref(false);
const revoking     = ref(null);

const showCreate        = ref(false);
const newLabel          = ref('');
const showToken         = ref(false);
const revealedToken     = ref('');
const revealedLabel     = ref('');
const copied            = ref(false);
const showRevokeConfirm = ref(false);
const revokeTarget      = ref(null);

const baseUrl      = (process.env.VUE_APP_API_URL || '').replace(/\/api\/?$/, '');
const exampleResponse = JSON.stringify({
    success: true,
    data: {
        id: 1,
        label: 'Truck 01',
        customer_reference: 'FLEET-001',
        serial: '850005000303',
        status: 'moving',
        location: {latitude: 33.749, longitude: -84.388, heading: 90, speed_mph: 45.2},
        battery_bars: 4,
        last_seen_at: '2026-08-01T14:30:00.000000Z',
    },
}, null, 2);

onMounted(loadCredentials);

async function loadCredentials() {
    loading.value = true;
    try {
        const res = await send('get', 'company-api-credentials');
        if (res.data?.success) {
            credentials.value = res.data.data ?? [];
        }
    } catch {
        snackbar.showSnackbar('Failed to load API keys.', 'error');
    } finally {
        loading.value = false;
    }
}

async function createCredential() {
    if (!newLabel.value.trim()) return;
    creating.value = true;
    try {
        const res = await send('post', 'company-api-credentials', {label: newLabel.value.trim()});
        if (res.data?.success) {
            revealedToken.value = res.data.data.token;
            revealedLabel.value = res.data.data.label;
            copied.value = false;
            showCreate.value = false;
            showToken.value = true;
            newLabel.value = '';
            await loadCredentials();
        } else {
            snackbar.showSnackbar(res.data?.message || 'Failed to create API key.', 'error');
        }
    } catch (e) {
        snackbar.showSnackbar(e?.response?.data?.message || 'Failed to create API key.', 'error');
    } finally {
        creating.value = false;
    }
}

function cancelCreate() {
    showCreate.value = false;
    newLabel.value = '';
}

async function copyToken() {
    try {
        await navigator.clipboard.writeText(revealedToken.value);
        copied.value = true;
        setTimeout(() => { copied.value = false; }, 2000);
    } catch {
        snackbar.showSnackbar('Could not copy — select and copy manually.', 'warning');
    }
}

function closeToken() {
    showToken.value = false;
    revealedToken.value = '';
}

function confirmRevoke(cred) {
    revokeTarget.value = cred;
    showRevokeConfirm.value = true;
}

async function doRevoke() {
    if (!revokeTarget.value) return;
    revoking.value = revokeTarget.value.id;
    try {
        const res = await send('delete', `company-api-credentials/${revokeTarget.value.id}`);
        if (res.data?.success) {
            snackbar.showSnackbar('API key revoked.');
            showRevokeConfirm.value = false;
            revokeTarget.value = null;
            await loadCredentials();
        } else {
            snackbar.showSnackbar(res.data?.message || 'Failed to revoke key.', 'error');
        }
    } catch (e) {
        snackbar.showSnackbar(e?.response?.data?.message || 'Failed to revoke key.', 'error');
    } finally {
        revoking.value = null;
    }
}

function credSubtitle(cred) {
    const created = new Date(cred.created_at).toLocaleDateString();
    const used = cred.last_used_at
        ? 'Last used ' + new Date(cred.last_used_at).toLocaleDateString()
        : 'Never used';
    return `Created ${created} · ${used}`;
}
</script>
